/**
 * Trace / observability backend form — LangSmith, Langfuse, OpenTelemetry, or Other.
 * Non-secrets stay on the form; secrets stay in a separate credentials object.
 * Connection fields and API keys are persisted on gate0_data.traces so Gate 2
 * can reuse them (same pattern as the API surface's base_url / method / path).
 */

export const TRACE_PROVIDER_OPTIONS = [
  { value: "langsmith", label: "LangSmith" },
  { value: "langfuse", label: "Langfuse" },
  { value: "opentelemetry", label: "OpenTelemetry" },
  { value: "other", label: "Other" },
];

export const TRACE_PROVIDERS = TRACE_PROVIDER_OPTIONS.map((option) => option.value);

export const EMPTY_TRACE_FORM = {
  provider: "",
  project: "",
  workspace: "",
  endpoint: "",
  baseUrl: "",
};

export const EMPTY_TRACE_CREDENTIALS = {
  apiKey: "",
  publicKey: "",
  headers: "",
};

export const TRACE_PROVIDER_FIELDS = {
  langsmith: [
    {
      key: "apiKey",
      label: "API key",
      required: true,
      credential: true,
      placeholder: "lsv2_pt_…",
    },
    { key: "project", label: "Project", required: true, placeholder: "my-agent-project" },
    { key: "workspace", label: "Workspace", required: false, placeholder: "optional" },
  ],
  langfuse: [
    {
      key: "baseUrl",
      label: "Host",
      required: true,
      placeholder: "https://cloud.langfuse.com",
      fullWidth: true,
    },
    {
      key: "publicKey",
      label: "Public key",
      required: true,
      credential: true,
      placeholder: "pk-lf-…",
    },
    {
      key: "apiKey",
      label: "Secret key",
      required: true,
      credential: true,
      placeholder: "sk-lf-…",
    },
    { key: "project", label: "Project", required: false, placeholder: "optional" },
  ],
  opentelemetry: [
    {
      key: "endpoint",
      label: "OTLP endpoint",
      required: true,
      placeholder: "https://otel.example.com/v1/traces",
      fullWidth: true,
    },
    {
      key: "headers",
      label: "Headers",
      required: false,
      credential: true,
      placeholder: "Authorization: Bearer … (optional)",
      fullWidth: true,
    },
  ],
  other: [
    {
      key: "baseUrl",
      label: "Base URL",
      required: true,
      placeholder: "https://traces.example.com",
      fullWidth: true,
    },
    {
      key: "apiKey",
      label: "API key",
      required: true,
      credential: true,
      placeholder: "API key",
    },
    { key: "project", label: "Project / dataset", required: false, placeholder: "optional" },
  ],
};

export function normalizeTraceProvider(value) {
  const raw = String(value || "").trim().toLowerCase();
  return TRACE_PROVIDERS.includes(raw) ? raw : "";
}

export function fieldsForTraceProvider(provider) {
  const id = normalizeTraceProvider(provider);
  return id ? TRACE_PROVIDER_FIELDS[id] || [] : [];
}

export function credentialFieldForTraceProvider(provider) {
  return fieldsForTraceProvider(provider).find((field) => field.credential && field.required) || null;
}

export function traceFormComplete(form, credentials = {}) {
  const provider = normalizeTraceProvider(form?.provider);
  if (!provider) return false;
  return fieldsForTraceProvider(provider).every((field) => {
    if (!field.required) return true;
    const value = field.credential ? credentials[field.key] : form[field.key];
    return Boolean(String(value ?? "").trim());
  });
}

/** Provider, non-secret fields, and secrets — required before Gate 2 can reuse this connection. */
export function traceFormCanSave(form, credentials = {}) {
  return traceFormComplete(form, credentials);
}

export function traceFormFromGate1Config(config = {}) {
  const provider = normalizeTraceProvider(config.provider);
  return {
    ...EMPTY_TRACE_FORM,
    provider,
    project: String(config.project || "").trim(),
    workspace: String(config.workspace || "").trim(),
    endpoint: String(config.endpoint || "").trim(),
    baseUrl: String(config.baseUrl || config.base_url || "").trim(),
  };
}

export function traceFormFromGate0Data(gate0 = {}) {
  const summary = gate0?.gate1_trace_readiness_summary;
  if (summary && typeof summary === "object" && summary.provider) {
    return traceFormFromGate1Config(summary);
  }
  const declared = gate0?.traces;
  if (declared && typeof declared === "object" && declared.provider) {
    return traceFormFromGate1Config({
      provider: declared.provider,
      project: declared.project,
      workspace: declared.workspace,
      endpoint: declared.endpoint,
      baseUrl: declared.baseUrl || declared.base_url,
    });
  }
  return { ...EMPTY_TRACE_FORM };
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

/** Hydrate saved LangSmith / Langfuse keys from gate0_data.traces. */
export function traceCredentialsFromConfig(config = {}) {
  return {
    apiKey: firstNonEmpty(config.apiKey, config.api_key),
    publicKey: firstNonEmpty(config.publicKey, config.public_key),
    headers: firstNonEmpty(config.headers),
  };
}

export function traceCredentialsFromGate0Data(gate0 = {}) {
  return traceCredentialsFromConfig(gate0?.traces || {});
}

/**
 * Persist trace connection on gate0_data.traces, including the API key,
 * the same way the API surface stores base_url / endpoints / method.
 */
export function mergeTraceFormIntoGate0Data(gate0 = {}, form, credentials = {}) {
  const provider = normalizeTraceProvider(form?.provider);
  if (!provider) return { ...gate0 };
  const previous = gate0.traces && typeof gate0.traces === "object" ? gate0.traces : {};
  const traces = {
    provider,
    project: String(form.project || "").trim() || undefined,
    workspace: String(form.workspace || "").trim() || undefined,
    endpoint: String(form.endpoint || "").trim() || undefined,
    base_url: String(form.baseUrl || "").trim() || undefined,
  };
  const apiKey = firstNonEmpty(credentials.apiKey, previous.api_key, previous.apiKey);
  const publicKey = firstNonEmpty(credentials.publicKey, previous.public_key, previous.publicKey);
  const headers = firstNonEmpty(credentials.headers, previous.headers);
  if (apiKey) traces.api_key = apiKey;
  if (publicKey) traces.public_key = publicKey;
  if (headers) traces.headers = headers;
  return { ...gate0, traces };
}

/** Drop the saved trace backend so Gate 2 no longer treats it as declared. */
export function clearTraceFromGate0Data(gate0 = {}) {
  const next = { ...gate0 };
  delete next.traces;
  delete next.gate1_trace_readiness_summary;
  return next;
}

export function buildTraceSummary(form, { overallStatus, previous = null } = {}) {
  const provider = normalizeTraceProvider(form?.provider);
  const keepReady =
    previous &&
    normalizeTraceProvider(previous.provider) === provider &&
    String(previous.overall_status || "").toLowerCase() === "ready";
  const summary = {
    overall_status: overallStatus || (keepReady ? "ready" : "saved"),
    generated_at: keepReady && !overallStatus ? previous.generated_at : new Date().toISOString(),
    provider,
    project: String(form.project || "").trim() || null,
    workspace: String(form.workspace || "").trim() || null,
    endpoint: String(form.endpoint || "").trim() || null,
    baseUrl: String(form.baseUrl || "").trim() || null,
    base_url: String(form.baseUrl || "").trim() || null,
  };
  if (keepReady) {
    return {
      ...previous,
      ...summary,
      success: previous.success === true,
      connected: previous.connected === true,
    };
  }
  return summary;
}

export function isTraceProbeReport(report) {
  const status = String(report?.overall_status || "").toLowerCase();
  return status === "ready" || status === "failed";
}

export function isTraceSuccess(report) {
  return (
    report?.success === true ||
    String(report?.overall_status || "").toLowerCase() === "ready"
  );
}

export function connectionSummaryRowsForTrace(form) {
  return fieldsForTraceProvider(form?.provider)
    .filter((field) => !field.credential)
    .map((field) => ({ key: field.key, label: field.label, value: form[field.key] }))
    .filter((row) => String(row.value ?? "").trim());
}

/**
 * Request payload for POST /gate2/execution/collect-evidence (traces_config)
 * and POST /readiness/trace-check. Secrets stay in a separate credentials object
 * and are not echoed on GET summaries.
 */
export function buildTraceProbeConfig(form, credentials = {}) {
  return {
    provider: normalizeTraceProvider(form?.provider),
    project: String(form?.project || "").trim(),
    workspace: String(form?.workspace || "").trim(),
    endpoint: String(form?.endpoint || "").trim(),
    baseUrl: String(form?.baseUrl || "").trim(),
    apiKey: credentials.apiKey || "",
    publicKey: credentials.publicKey || "",
    headers: credentials.headers || "",
  };
}

export function buildTracePayload(form, credentials = {}) {
  const provider = normalizeTraceProvider(form?.provider);
  return {
    traces_config: {
      provider,
      project: String(form.project || "").trim(),
      workspace: String(form.workspace || "").trim(),
      endpoint: String(form.endpoint || "").trim(),
      baseUrl: String(form.baseUrl || "").trim(),
    },
    credentials: {
      apiKey: credentials.apiKey || "",
      publicKey: credentials.publicKey || "",
      headers: credentials.headers || "",
    },
  };
}
